/**
 * Role-Based Route Guard Component
 * 
 * Protects routes based on user roles and redirects unauthorized users.
 */

import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppShellSkeleton } from "../common/SkeletonLoader";
import {
  getDashboardRouteByRole,
  resolveRoleKey,
  selectUser,
  selectIsAuthenticated,
  selectIsInitializing,
} from "../../auth";
import { authLoginUrl } from "../../config/routePaths";

/**
 * Role-Based Route Guard
 * @param {Object} props
 * @param {ReactNode} props.children - Child components to render
 * @param {string|string[]} props.allowedRoles - Role(s) allowed to access this route
 * @param {string} props.redirectTo - Route to redirect unauthorized users (default: /app/dashboard)
 */
export default function RoleBasedRoute({ children, allowedRoles, redirectTo = "/app/dashboard" }) {
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isInitializing = useSelector(selectIsInitializing);
  const location = useLocation();

  // Show loading spinner while checking authentication state
  if (isInitializing) {
    return <AppShellSkeleton message="Loading…" />;
  }

  if (!isAuthenticated) {
    const returnTo = `${location.pathname}${location.search || ""}`;
    return <Navigate to={authLoginUrl(returnTo)} replace state={{ from: location }} />;
  }

  // Check if user has required role
  if (user) {
    const normalizedUserRole = resolveRoleKey(user);
    const normalizedAllowedRoles = Array.isArray(allowedRoles)
      ? allowedRoles.map(r => r.toLowerCase())
      : [allowedRoles.toLowerCase()];

    // Check if user role matches any allowed role
    const hasAccess = normalizedAllowedRoles.includes(normalizedUserRole);

    if (!hasAccess) {
      const defaultRedirect = getDashboardRouteByRole(normalizedUserRole);
      return <Navigate to={defaultRedirect || redirectTo} replace />;
    }
  }

  return children;
}

/**
 * Helper function to get dashboard route based on role
 */
export const getDashboardRoute = (role) => {
  return getDashboardRouteByRole(resolveRoleKey({ role }));
};

